import React from "react";
import { toast } from "sonner";

/**
 * Safely extracts a displayable message string or React node from any input.
 * Returns null if the message is empty, null, undefined, or an empty object.
 */
const resolveMessage = (msg) => {
  if (msg === null || msg === undefined) return null;
  if (typeof msg === "string") {
    const trimmed = msg.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof msg === "number") return String(msg);
  if (React.isValidElement(msg)) return msg;
  if (typeof msg === "object") {
    const candidate =
      msg.response?.data?.message ||
      msg.response?.data?.msg ||
      msg.response?.data?.error ||
      msg.data?.message ||
      msg.message ||
      msg.msg ||
      msg.error;

    if (typeof candidate === "string") {
      const trimmed = candidate.trim();
      return trimmed.length > 0 ? trimmed : null;
    }
    if (React.isValidElement(candidate)) return candidate;
  }
  return null;
};

/**
 * Checks whether an error toast should be suppressed
 * (e.g. aborted/canceled requests, or session errors during logout/login).
 */
const shouldSuppressError = (text, rawError) => {
  if (!text) return true;

  const textStr = typeof text === "string" ? text.toLowerCase() : "";

  // Suppress server-down / network errors to prevent error popups when backend is offline
  if (
    textStr.includes("network error") ||
    textStr.includes("failed to fetch") ||
    textStr.includes("err_network") ||
    textStr.includes("err_connection_refused") ||
    textStr.includes("econnrefused") ||
    rawError?.code === "ERR_NETWORK" ||
    rawError?.code === "ECONNREFUSED" ||
    rawError?.message === "Network Error"
  ) {
    return true;
  }

  // Ignore aborted / canceled requests
  if (
    textStr === "canceled" ||
    textStr.includes("cancelederror") ||
    textStr.includes("aborterror") ||
    textStr.includes("request aborted") ||
    rawError?.name === "CanceledError" ||
    rawError?.name === "AbortError" ||
    rawError?.code === "ERR_CANCELED"
  ) {
    return true;
  }

  // Suppress session/auth errors on logout or login pages
  const currentPath = typeof window !== "undefined" ? window.location.pathname : "";
  const isAuthOrLogout =
    currentPath.includes("logout") ||
    currentPath.includes("login") ||
    currentPath.includes("auth-");

  if (isAuthOrLogout && typeof text === "string") {
    const lower = text.toLowerCase();
    if (
      lower.includes("session expired") ||
      lower.includes("token is missing") ||
      lower.includes("invalid credentials") ||
      lower.includes("unauthorized") ||
      lower.includes("forbidden")
    ) {
      return true;
    }
  }

  return false;
};

/**
 * Universal Toast Notification Adapter
 * Powered by Sonner (modern card deck, spring animations, rich colors)
 * 100% backward-compatible with all existing ToastMessage calls across the project.
 */
export const ToastMessage = {
  // 1. Success Toast
  Success: (message, options = 3000) => {
    const content = resolveMessage(message);
    if (!content) return null;

    const isDuration = typeof options === "number";
    const duration = isDuration ? options : options?.duration || 3000;
    const description = typeof options === "object" ? options?.description : undefined;
    const action = typeof options === "object" ? options?.action : undefined;

    return toast.success(content, {
      duration,
      description,
      action,
    });
  },

  // 2. Error Toast
  Error: (message, options = 4000) => {
    const content = resolveMessage(message);
    if (!content || shouldSuppressError(content, message)) {
      return null;
    }

    const isDuration = typeof options === "number";
    const duration = isDuration ? options : options?.duration || 4000;
    const description = typeof options === "object" ? options?.description : undefined;
    const action = typeof options === "object" ? options?.action : undefined;

    return toast.error(content, {
      duration,
      description,
      action,
    });
  },

  // 3. Info Toast
  Info: (message, options = 3000) => {
    const content = resolveMessage(message);
    if (!content) return null;

    const isDuration = typeof options === "number";
    const duration = isDuration ? options : options?.duration || 3000;
    const description = typeof options === "object" ? options?.description : undefined;
    const action = typeof options === "object" ? options?.action : undefined;

    return toast.info(content, {
      duration,
      description,
      action,
    });
  },

  // 4. Warning Toast
  Warning: (message, options = 3000) => {
    const content = resolveMessage(message);
    if (!content) return null;

    const isDuration = typeof options === "number";
    const duration = isDuration ? options : options?.duration || 3000;
    const description = typeof options === "object" ? options?.description : undefined;
    const action = typeof options === "object" ? options?.action : undefined;

    return toast.warning(content, {
      duration,
      description,
      action,
    });
  },

  // 5. Promise-based Toast (Loading -> Success / Error)
  Promise: (promiseOrFn, messages = {}) => {
    const {
      pending = "Loading...",
      success = "Done!",
      error = "Something went wrong",
    } = messages;

    const promise =
      typeof promiseOrFn === "function"
        ? promiseOrFn()
        : Promise.resolve(promiseOrFn);

    toast.promise(promise, {
      loading: pending,
      success: (data) => {
        const res = typeof success === "function" ? success(data) : success;
        return resolveMessage(res) || "Done!";
      },
      error: (err) => {
        const res = typeof error === "function" ? error(err) : error;
        return resolveMessage(res) || "Something went wrong";
      },
    });

    return promise;
  },

  // 6. Action Toast (convenience for quick Undo / Action buttons)
  Action: (message, actionOptions = {}) => {
    const content = resolveMessage(message);
    if (!content) return null;

    return toast(content, {
      duration: actionOptions.duration || 4000,
      description: actionOptions.description,
      action: actionOptions.action, // e.g. { label: "Undo", onClick: () => {} }
    });
  },

  // 7. Dismiss All or by ID
  Dismiss: (id) => {
    toast.dismiss(id);
  },
};

export default ToastMessage;
